import { GoMotoComponent } from '../../../../../API/GoMoto.types';
import { useAdminDispatch } from '../../../../../context/Admin/admin';
import { AdminDispatchState, AdminDispatchTypes } from '../../../../../context/Admin/admin.types';
import { useAdminLockerOperationDispatch } from '../../../../../context/AdminLockerOperation/adminLockerOperation';
import { AdminLockerOperationDispatch, AdminLockerOperationDispatchTypes } from '../../../../../context/AdminLockerOperation/adminLockerOperation.types';
import { useLockerOperation } from '../../../../../context/Appointment/appointment';
import { useKioskState } from '../../../../../context/Kiosk/kiosk';
import { useLocalization } from '../../../../../context/Localization/localization';
import { LocalizationType } from '../../../../../context/Localization/localization.types';
import { usePageDispatch } from '../../../../../context/Page/page';
import { Types } from '../../../../../context/Page/page.types';
import { CTAButton } from '../../../../../controls/ButtonBase';
import { GMButtonGroup } from '../../../../../controls/ButtonGroup';
import { GMDialogError, GMDialogRetryCancel } from '../../../../../controls/Dialog';
import { GMSizedIcon } from '../../../../../controls/Icon';
import { Spinner } from '../../../../../controls/Spinner/Spinner';
import { ScrollableBody } from '../../../../Layout/ScrollableBody/ScrollableBody';
import { KeyManagementHeader } from '../../../../../controls/KeyManagementHeader/KeyManagementHeader';
import css from './StoreKey.module.scss';
import { useState, useEffect } from 'react';
import { StoreKeyProps } from './StoreKey.types';
import { KeyDropError } from '../../../../../context/Appointment/appointment.types';
import { ILockerFixture } from '../../../../../context/lockers';

/**
 * Starts and handles the state management during the store key process
 * for the current appointment.
 *
 * @param onErrorClose - {@link StoreKeyProps.onErrorClose onErrorClose} description.
 * @param onProceed - {@link StoreKeyProps.onProceed onProceed} description.
 */
export const StoreKey: GoMotoComponent<StoreKeyProps> = ({
    onErrorClose,
    onProceed,
}) => {
    const Localization: LocalizationType = useLocalization();
    const { keyStorageType } = useKioskState();
    const adminDispatch: AdminDispatchState = useAdminDispatch();
    const adminLockerOperationDispatch: AdminLockerOperationDispatch = useAdminLockerOperationDispatch();
    const pageDispatch = usePageDispatch();
    const { start, locker, inProgress, complete, error: keyStoreError } = useLockerOperation({ byAdvisor: true });

    const [previousLockerNumber, setPreviousLockerNumber] = useState<ILockerFixture['Id'] | undefined>(undefined);
    const [error, setError] = useState<string | undefined>(undefined);
    const [retryError, setRetryError] = useState<string | undefined>(undefined);

    useEffect(() => {
        if (inProgress && !!locker && previousLockerNumber === undefined) {
            setPreviousLockerNumber(locker.Id);
        }
    }, [inProgress, locker, previousLockerNumber]);

    useEffect(() => {
        pageDispatch({
            type: Types.SetEvokeOnStillActive,
            payload: () => {
                if (complete) {
                    onProceed();
                }
            },
        });
        pageDispatch({
            type: Types.SetEvokeOnTimeout,
            payload: () => {
                if (complete) {
                    onProceed();

                    return false;
                }

                return true;
            },
        });

        return () => {
            pageDispatch({
                type: Types.SetEvokeOnStillActive,
                payload: undefined,
            });
            pageDispatch({
                type: Types.SetEvokeOnTimeout,
                payload: undefined,
            });
        };
    }, [complete, onProceed, pageDispatch]);

    useEffect(() => {
        if (!complete && !inProgress && !error) {
            start({ lockerAction: 'store-key' });
        }
    }, [complete, error, inProgress, start]);

    useEffect(() => {
        if (complete && locker) {
            adminDispatch({
                type: AdminDispatchTypes.SetLockerChanged,
                payload: locker,
            });
            adminLockerOperationDispatch({
                type: AdminLockerOperationDispatchTypes.SetKeyStored,
                payload: true,
            });
        }
    }, [complete, locker, adminDispatch, adminLockerOperationDispatch]);

    useEffect(() => {
        if (keyStoreError !== KeyDropError.none) {
            switch (keyStoreError) {
                case KeyDropError.noLockerAvailable:
                    setError(Localization.NoLockerAvailable);
                    break;
                case KeyDropError.keyAlreadyStored:
                    setError(Localization.KeyAlreadyAssigned);
                    break;
                case KeyDropError.failedToOpen:
                    setRetryError(Localization.FailedToOpenLocker);
                    break;
                default:
                    setError(Localization.AnUnknownErrorHasOccurred);
                    break;
            }
        }
    }, [Localization.AnUnknownErrorHasOccurred, Localization.FailedToOpenLocker, Localization.KeyAlreadyAssigned, Localization.NoLockerAvailable, keyStoreError]);

    const onRetryOpenLocker: VoidFunction = () => {
        setRetryError(undefined);
        start({
            lockerAction: 'store-key',
            resetAttempts: true,
        });
    };

    return (
        <ScrollableBody
            heading={
                <KeyManagementHeader
                    error={keyStorageType === 'keydrop' ? Localization.PlaceKeyInSlotNow : Localization.PlaceKeyInLockerNow}
                    secondaryHeading={locker || previousLockerNumber !== undefined ? (
                        (keyStorageType === 'keydrop' ? (
                            Localization.formatString(Localization.LoadKeyIntoSlot, locker ? locker.Id : previousLockerNumber ?? 0)
                        ) : (
                            Localization.formatString(
                                Localization.LoadKeyIntoLocker,
                                <b className={css.highlight}>{Localization.formatString(Localization.LockerNum, locker ? locker.Id : previousLockerNumber ?? 0)}</b>,
                            )
                        ))
                    ) : undefined}
                    showMustache
                />
            }
            proceedButtons={
                <GMButtonGroup
                    buttons={[
                        <CTAButton
                            disabled={!complete}
                            styleType={'primary'}
                            onClick={async () => {
                                onProceed();
                            }}
                            label={Localization.Finish}
                            kioskOptions={{ inlineOptions: { sizedIcon: <GMSizedIcon iconSize={'custom'} iconType={'Complete'} /> } }}
                            id={Localization.Finish}
                            ariaLabel={Localization.Finish}
                        />,
                    ]}
                />
            }
        >
            <div>
                {inProgress && <Spinner />}
                <GMDialogError
                    ariaLabel={Localization.AnErrorHasOccured}
                    open={!!error}
                    onClose={() => {
                        setError(undefined);
                        onErrorClose();
                    }}
                >
                    {error}
                </GMDialogError>
                <GMDialogRetryCancel
                    ariaLabel={Localization.Retry}
                    open={!!retryError}
                    onCancel={() => {
                        setRetryError(undefined);
                        onErrorClose();
                    }}
                    onRetry={onRetryOpenLocker}
                >
                    {retryError}
                </GMDialogRetryCancel>
            </div>
        </ScrollableBody>
    );
};
